import store from '@/store';
import { openExternalUrl, isAbsoluteUrl } from '@/utils/linkUtils';
import { isRedirectUrlAllowed } from '@/utils/redirectUrlValidator';
import handleNavigation from '@/utils/navigationUtils';
import storageService from '@/modules/common/services/storageService';
import logError from '@/utils/logError/logError';

// See pushNotificationClick.md for the module overview, the relative-vs-external contract, and
// the login-round-trip stash mechanism.
export const NOTIFICATION_UID_QUERY_KEY = 'nc_notification_uid';
export const REDIRECT_URL_QUERY_KEY = 'nc_redirect_url';

// 10 minutes is generous for an actual login round trip but short enough that an abandoned
// attempt can't misfire into some unrelated later session.
const PENDING_VALUE_TTL_MS = 10 * 60 * 1000;
const PENDING_NOTIFICATION_UID_STORAGE_KEY = 'pending_push_notification_uid';
const PENDING_REDIRECT_URL_STORAGE_KEY = 'pending_push_redirect_url';

/**
 * @param {string} [search] a query string; defaults to the current document's
 * @returns {string} the clicked notification uid, or '' when this was not opened from a push
 */
export const getClickedPushNotificationUid = (search = window.location.search) => {
  const params = new URLSearchParams(search || '');
  return (params.get(NOTIFICATION_UID_QUERY_KEY) || '').trim();
};

/**
 * @param {string} [search] a query string; defaults to the current document's
 * @returns {string} the push's redirect target (relative or absolute), or '' when there is none
 */
export const getPushRedirectUrl = (search = window.location.search) => {
  const params = new URLSearchParams(search || '');
  return (params.get(REDIRECT_URL_QUERY_KEY) || '').trim();
};

// Must be called before App.vue awaits getToken() - see pushNotificationClick.md.
export const stashPendingPushParamsFromUrl = (search = window.location.search) => {
  const notificationUid = getClickedPushNotificationUid(search);
  const redirectUrl = getPushRedirectUrl(search);
  if (notificationUid) storageService.saveWithExpiry(PENDING_NOTIFICATION_UID_STORAGE_KEY, notificationUid, PENDING_VALUE_TTL_MS);
  if (redirectUrl) storageService.saveWithExpiry(PENDING_REDIRECT_URL_STORAGE_KEY, redirectUrl, PENDING_VALUE_TTL_MS);
};

// Fire-and-forget; see pushNotificationClick.md.
export const reportPushNotificationClickFromUrl = () => {
  const notificationUid = getClickedPushNotificationUid() || storageService.getWithExpiry(PENDING_NOTIFICATION_UID_STORAGE_KEY) || '';
  storageService.remove(PENDING_NOTIFICATION_UID_STORAGE_KEY);
  if (!notificationUid) return;

  store.dispatch('NotificationCenterStore/reportPushNotificationClick', notificationUid);
};

export const redirectPushClickFromUrl = () => {
  const redirectUrl = getPushRedirectUrl() || storageService.getWithExpiry(PENDING_REDIRECT_URL_STORAGE_KEY) || '';
  storageService.remove(PENDING_REDIRECT_URL_STORAGE_KEY);
  if (!redirectUrl) return;

  if (!isAbsoluteUrl(redirectUrl)) {
    handleNavigation({ path: redirectUrl });
    return;
  }

  if (!isRedirectUrlAllowed(redirectUrl)) {
    logError('Blocked push redirect to a non-whitelisted domain', new Error('nc_redirect_url domain not allowed'), { redirectUrl });
    return;
  }

  openExternalUrl(redirectUrl);
};

export default {
  getClickedPushNotificationUid,
  getPushRedirectUrl,
  stashPendingPushParamsFromUrl,
  reportPushNotificationClickFromUrl,
  redirectPushClickFromUrl,
};
